import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearch } from "wouter";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useCompradores, useCreateComprador, useProdutosVenda,
  useVenda, useCreateVenda, useUpdateVenda, useEmitirVenda, useColheitasPorDiaSetor,
  podeEditarVenda,
  type ColheitaPorDiaSetor,
} from "@/hooks/use-vendas";
import type { ProdutoVenda, ModoPeso } from "@shared/schema";
import { resolverPeriodoEdicaoVenda, periodoDoPreset, somarDias } from "@shared/vendas-periodo";
import { checarVencimento, mensagemDeFalhaDeData } from "@shared/mercado";
import { hojeNaFazenda } from "@/lib/data-fazenda";

import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { CardPracaHoje, usePrecoDaPraca } from "@/components/mercado/card-praca-hoje";
type ItemState = { quantidadeCaixas: number; precoKg: string; pesoCaixaKg: string; kg: string };

/** Aceita vírgula decimal (o teclado do celular manda "2,30"). */
function parseNum(v: string | number): number {
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
}

/** kg vindo do Postgres chega como decimal de duas casas ("4942.00"). É o campo herói do modo
 *  novo e o primeiro que se vê ao reabrir a venda — abre "4942", não "4942.00". Só a EXIBIÇÃO
 *  inicial: o que for digitado depois passa por `parseNum` normalmente.
 *  (`precoKg` fica como está: duas casas em dinheiro estão certas.) */
function kgParaExibicao(v: string): string {
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("pt-BR", { useGrouping: false, maximumFractionDigits: 2 });
}

export default function VendaFormPage() {
  const { id } = useParams<{ id?: string }>();
  const search = useSearch();
  const { voltar, substituir } = useNavegacao();
  const { toast } = useToast();
  const isEditing = !!id;

  const dataPreenchida = new URLSearchParams(search).get("data");

  const { data: venda, isLoading: carregandoVenda } = useVenda(id);
  const { data: produtos = [], isLoading: carregandoProdutos } = useProdutosVenda(true);
  const { data: compradores = [], isLoading: carregandoCompradores } = useCompradores(true);
  const createComprador = useCreateComprador();
  const createVenda = useCreateVenda();
  const updateVenda = useUpdateVenda();
  const emitirVenda = useEmitirVenda();

  // `hojeNaFazenda()`, nunca `new Date().toISOString()`: das 21:00 às 23:59 na Bahia (UTC-3)
  // o UTC já virou o dia seguinte, e o Hugo opera o app à noite, quando o caminhão fecha.
  // Aqui isso entrava em `vendas.data`, no `ano` (virada de ano inclusive), no romaneio e nos
  // filtros — e o preset "Hoje" apontava pra um dia sem colheita nenhuma.
  const [data, setData] = useState(dataPreenchida ?? hojeNaFazenda());
  const [compradorId, setCompradorId] = useState("");
  /** "" = a definir. Vira `null` no payload — ver `buildPayload`. */
  const [vencimento, setVencimento] = useState("");
  const [novoCompradorNome, setNovoCompradorNome] = useState("");
  const [motorista, setMotorista] = useState("");
  const [placa, setPlaca] = useState("");
  const [preset, setPreset] = useState<"hoje" | "ontem-hoje" | "custom">(dataPreenchida ? "hoje" : "ontem-hoje");
  // O estado inicial já produz o que o efeito de preset produzia na montagem: "ontem + hoje"
  // pra venda nova, e o dia certo quando vem `?data=` da tela de Colheitas. É por isso que
  // apagar aquele efeito não regride a criação.
  const [colheitaInicio, setColheitaInicio] = useState(dataPreenchida ?? somarDias(data, -1));
  const [colheitaFim, setColheitaFim] = useState(dataPreenchida ?? data);
  const [itens, setItens] = useState<Record<string, ItemState>>({});
  // Modo da CARGA (não do item): nenhuma venda mista existe em produção (0 de 32), e um
  // seletor por linha convidaria a criar a primeira por acidente.
  const [modoPeso, setModoPeso] = useState<ModoPeso>("CAIXA_PADRAO");
  const [confirmarVoltaCaixa, setConfirmarVoltaCaixa] = useState(false);
  // Chaves "data|setorId" marcadas para associação — vira `colheitaAssociacoes` no payload.
  const [associacoesMarcadas, setAssociacoesMarcadas] = useState<Set<string>>(new Set());
  // Cache de rótulo (setorNome, cachos) de todo grupo (dia,setor) já visto nesta sessão de edição —
  // inclusive um que tenha ficado fora do período atual. Sem isso, ao estreitar o período perdemos
  // como desenhar a linha "fora do período" (ela sai da resposta de useColheitasPorDiaSetor).
  const [infoGrupos, setInfoGrupos] = useState<Map<string, { data: string; setorNome: string; cachos: number }>>(new Map());
  // Evita re-selecionar tudo a cada refetch em segundo plano; só quando o período muda de fato.
  const periodoAutoSelecionadoRef = useRef<string | null>(null);
  // Em edição, a pré-seleção automática só pode agir DEPOIS de o usuário mexer no período.
  // Sem isso, o simples load da tela substituía a seleção salva.
  const periodoMexidoPeloUsuario = useRef(false);
  // Trava síncrona contra duplo clique/toque — `salvando` (isPending) só reflete no DOM após
  // o próximo re-render, deixando uma janela de corrida para disparo duplo antes do disabled.
  const submetendoRef = useRef(false);

  const { data: colheitasPorDiaSetor } = useColheitasPorDiaSetor(colheitaInicio, colheitaFim);

  // Preenche o form ao carregar uma venda existente (edição).
  useEffect(() => {
    if (!venda) return;
    setData(venda.data);
    setCompradorId(venda.compradorId);
    // Sem isto, abrir a venda para corrigir uma placa mandaria `null` e apagaria
    // o prazo que alguém definiu pelo card ou por "A receber".
    setVencimento(venda.vencimento ?? "");
    setMotorista(venda.motorista ?? "");
    setPlaca(venda.placa ?? "");
    const { inicio, fim } = resolverPeriodoEdicaoVenda(venda);
    setColheitaInicio(inicio);
    setColheitaFim(fim);
    setPreset("custom");
    setAssociacoesMarcadas(new Set((venda.colheitaAssociacoes ?? []).map(a => `${a.data}|${a.setorId}`)));
    setInfoGrupos(prev => {
      const next = new Map(prev);
      for (const a of venda.colheitaAssociacoes ?? []) {
        next.set(`${a.data}|${a.setorId}`, { data: a.data, setorNome: a.setor.nome, cachos: a.cachosNoMomento ?? 0 });
      }
      return next;
    });
    // Já resolvido a partir da venda salva — o efeito de pré-seleção não deve sobrescrever.
    periodoAutoSelecionadoRef.current = `${inicio}|${fim}`;
    const itensMap: Record<string, ItemState> = {};
    venda.itens.forEach(it => {
      // Pré-preenche com o peso/caixa SALVO no item (pode ser um override desta venda) —
      // não com o padrão do cadastro do produto, senão perderíamos um override já feito.
      itensMap[it.produtoVendaId] = {
        quantidadeCaixas: it.quantidadeCaixas,
        precoKg: it.precoKg,
        pesoCaixaKg: it.pesoCaixaKg ?? "",
        kg: kgParaExibicao(it.pesoTotalKg),
      };
    });
    setItens(itensMap);
    // O modo é DERIVADO dos itens — não há campo de modo em `vendas`, e não deve haver:
    // dois lugares guardando a mesma verdade divergem.
    setModoPeso(venda.itens.length > 0 && venda.itens.every(i => i.modoPeso === "PESAGEM") ? "PESAGEM" : "CAIXA_PADRAO");
  }, [venda]);

  // Só PAGA/CANCELADA são imutáveis (mesma regra do servidor em updateVenda) — RASCUNHO e
  // EMITIDA-não-paga podem editar itens e colheita associada normalmente. Sem este guard pras
  // duas que sobram, a tela continuava oferecendo os botões de salvar e o clique falhava com
  // 409 sem contexto (ver spec EditarColheitaAssociada).
  useEffect(() => {
    if (!venda || podeEditarVenda(venda)) return;
    toast({
      title: venda.status === "PAGA" ? "Venda paga não pode ser editada" : "Venda cancelada não pode ser editada",
      description: venda.numero ? `Nº ${String(venda.numero).padStart(3, "0")}/${venda.ano}` : undefined,
    });
    // `substituir`: o /editar que não pode existir sai do histórico (e a origem
    // da lista segue junto), senão o "voltar" do detalhe caía de volta no form.
    substituir(`/cacho/vendas/${venda.id}`);
  }, [venda]);

  // ⚠️ NÃO transformar isto num useEffect com deps [preset, data]. Era exatamente esse efeito
  // que destruía dado: efeito que deriva estado de estado SEMPRE roda uma vez na montagem,
  // com o closure do PRIMEIRO render. Numa venda já em cache (o caminho normal: vir do detalhe
  // pelo botão "Editar", com staleTime Infinity), o efeito de carga e este rodavam no mesmo
  // commit — a carga punha o período salvo (13/08–14/08) e este, ainda enxergando
  // preset="ontem-hoje" e data=hoje, sobrescrevia com 14/08–15/08. A tela abria com o chip
  // "Personalizar…" aceso e datas de "ontem + hoje", e a pré-seleção então trocava a seleção
  // salva pelos grupos do período errado. Bastava abrir e salvar pra perder a associação.
  // Como ação de clique não existe montagem, logo não existe a corrida.
  function aplicarPreset(p: "hoje" | "ontem-hoje" | "custom") {
    setPreset(p);
    const janela = periodoDoPreset(p, data);
    if (janela) {
      setColheitaInicio(janela.inicio);
      setColheitaFim(janela.fim);
    }
    periodoMexidoPeloUsuario.current = true;
  }

  // Mudar a data da VENDA desloca a janela de colheita — mas só em venda nova. Numa venda
  // salva, mexer na data da venda não pode mexer no período de colheita que já está gravado.
  function alterarDataVenda(nova: string) {
    setData(nova);
    if (isEditing) return;
    const janela = periodoDoPreset(preset, nova);
    if (janela) {
      setColheitaInicio(janela.inicio);
      setColheitaFim(janela.fim);
    }
  }

  function alterarPeriodoManual(qual: "inicio" | "fim", valor: string) {
    if (qual === "inicio") setColheitaInicio(valor);
    else setColheitaFim(valor);
    periodoMexidoPeloUsuario.current = true;
  }

  // Pré-seleção "todos marcados" (decisão §5.1 da spec): sempre que o período muda — inclusive
  // ao abrir o form a partir de um dia específico da página Colheitas — os grupos (dia,setor)
  // do novo período vêm todos marcados por padrão; o usuário desmarca o que não entrar na carga.
  useEffect(() => {
    if (!colheitasPorDiaSetor) return;
    // Em venda NOVA a pré-seleção é o comportamento desejado. Em EDIÇÃO ela só pode rodar
    // depois de o usuário mexer no período — senão substitui, no load, uma seleção que ele
    // levou tempo montando e que nem viu sumir.
    if (isEditing && !periodoMexidoPeloUsuario.current) return;

    const chavePeriodo = `${colheitaInicio}|${colheitaFim}`;
    if (periodoAutoSelecionadoRef.current === chavePeriodo) return;
    periodoAutoSelecionadoRef.current = chavePeriodo;

    const doPeriodo = colheitasPorDiaSetor.map(g => `${g.data}|${g.setorId}`);
    setAssociacoesMarcadas(prev => {
      // Em EDIÇÃO, MESCLA: ampliar ou deslocar o período não pode descartar o que já estava
      // associado. O que ficar fora do período novo aparece no bloco âmbar "Fora do período
      // selecionado" — visível e desmarcável, que é a invariante da PR #142: o payload nunca
      // contém uma associação que o usuário não podia ver e desmarcar.
      // Em CRIAÇÃO, substitui: não há nada a perder.
      if (!isEditing) return new Set(doPeriodo);
      const next = new Set(prev);
      for (const chave of doPeriodo) next.add(chave);
      return next;
    });
  }, [colheitaInicio, colheitaFim, colheitasPorDiaSetor, isEditing]);

  // Alimenta o cache de rótulos com cada grupo que a query trouxer — inclusive quando o
  // período depois estreitar e o grupo sumir da resposta.
  useEffect(() => {
    if (!colheitasPorDiaSetor) return;
    setInfoGrupos(prev => {
      const next = new Map(prev);
      for (const g of colheitasPorDiaSetor) {
        next.set(`${g.data}|${g.setorId}`, { data: g.data, setorNome: g.setorNome, cachos: g.cachos });
      }
      return next;
    });
  }, [colheitasPorDiaSetor]);

  /** As OUTRAS vendas que já reivindicam este grupo — a própria venda em edição sai fora. */
  function outrasVendasNoGrupo(g: ColheitaPorDiaSetor) {
    return g.associadoEm.filter(a => a.vendaId !== id);
  }

  function toggleAssociacao(diaData: string, setorId: string) {
    const chave = `${diaData}|${setorId}`;
    setAssociacoesMarcadas(prev => {
      const next = new Set(prev);
      if (next.has(chave)) next.delete(chave);
      else next.add(chave);
      return next;
    });
  }

  const gruposPorDia = useMemo(() => {
    const porDia = new Map<string, ColheitaPorDiaSetor[]>();
    for (const g of colheitasPorDiaSetor ?? []) {
      if (!porDia.has(g.data)) porDia.set(g.data, []);
      porDia.get(g.data)!.push(g);
    }
    return Array.from(porDia.entries());
  }, [colheitasPorDiaSetor]);

  const chavesVisiveis = useMemo(
    () => new Set((colheitasPorDiaSetor ?? []).map(g => `${g.data}|${g.setorId}`)),
    [colheitasPorDiaSetor],
  );

  // Associações marcadas que o usuário não pode ver (nem desmarcar) no seletor atual — o
  // período foi estreitado depois de marcadas, ou vieram salvas fora do período resolvido.
  // Nunca descartadas do payload em silêncio (C2); só aqui pra desenhar a rede de segurança.
  const associacoesForaDoPeriodo = useMemo(() => {
    return Array.from(associacoesMarcadas)
      .filter(chave => !chavesVisiveis.has(chave))
      .map(chave => {
        const [diaData, setorId] = chave.split("|");
        const info = infoGrupos.get(chave);
        return { chave, data: info?.data ?? diaData, setorId, setorNome: info?.setorNome ?? "setor desconhecido", cachos: info?.cachos ?? 0 };
      })
      .sort((a, b) => a.data.localeCompare(b.data));
  }, [associacoesMarcadas, chavesVisiveis, infoGrupos]);

  // Um romaneio de carga de 12/08 associado a colheita de 13/08 não faz sentido físico — a
  // carga saiu antes de a fruta ser colhida. Aconteceu na venda 033/2026 e ninguém foi
  // avisado. AVISO, nunca trava: existe caso legítimo (carga fechada na virada do dia, data
  // da venda digitada errada e ainda não corrigida), e transformar aviso em trava é como se
  // cria o hábito de contornar.
  const diasPosterioresAVenda = useMemo(() => {
    const dias = new Set<string>();
    for (const chave of Array.from(associacoesMarcadas)) {
      const diaData = chave.split("|")[0];
      if (diaData > data) dias.add(diaData);
    }
    return Array.from(dias).sort();
  }, [associacoesMarcadas, data]);

  // `associacoesMarcadas` guarda pares (dia, setor), não setores: B1 em 12/08 e B1 em 13/08
  // são duas entradas do MESMO setor. O `.size` dizia "4 setores" para 3 setores em 2 dias —
  // o número estava certo, o rótulo é que não. Conta as duas dimensões e nomeia cada uma.
  const resumoMarcados = useMemo(() => {
    const setoresDistintos = new Set<string>();
    const diasDistintos = new Set<string>();
    for (const chave of Array.from(associacoesMarcadas)) {
      const [diaData, setorId] = chave.split("|");
      diasDistintos.add(diaData);
      setoresDistintos.add(setorId);
    }
    const s = setoresDistintos.size;
    const d = diasDistintos.size;
    return `${s} setor${s !== 1 ? "es" : ""} · ${d} dia${d !== 1 ? "s" : ""}`;
  }, [associacoesMarcadas]);

  const totalCachosMarcados =
    (colheitasPorDiaSetor ?? [])
      .filter(g => associacoesMarcadas.has(`${g.data}|${g.setorId}`))
      .reduce((acc, g) => acc + g.cachos, 0)
    + associacoesForaDoPeriodo.reduce((acc, a) => acc + a.cachos, 0);

  const produtoMap = useMemo(() => new Map(produtos.map(p => [p.id, p])), [produtos]);
  // Preço da praça por produto de venda — a mesma tradução que o servidor usa
  // para agregar "minhas vendas × praça" (`produtoDoItem`, em shared). É a praça
  // da DATA DESTA VENDA: editar a de 13/09 em 25/09 sugere o preço de 13/09.
  const precoDaPraca = usePrecoDaPraca(produtos, data);

  const emPesagem = modoPeso === "PESAGEM";

  const itensAtivos = Object.entries(itens).filter(([, v]) =>
    emPesagem ? (parseNum(v.kg) > 0 || v.precoKg) : (v.quantidadeCaixas > 0 || v.precoKg));

  function pesoCaixaResolvido(v: ItemState, produto: ProdutoVenda): number {
    const editado = parseFloat(String(v.pesoCaixaKg).replace(",", "."));
    return editado > 0 ? editado : parseFloat(produto.pesoCaixaKg);
  }

  /** kg do item nos dois modos. Em PESAGEM é o valor digitado — nunca reconstruído. */
  function kgDoItem(v: ItemState, produto: ProdutoVenda): number {
    return emPesagem ? parseNum(v.kg) : v.quantidadeCaixas * pesoCaixaResolvido(v, produto);
  }

  const totais = itensAtivos.reduce(
    (acc, [produtoId, v]) => {
      const produto = produtoMap.get(produtoId);
      if (!produto) return acc;
      const kg = kgDoItem(v, produto);
      const preco = parseNum(v.precoKg);
      // Em pesagem a caixa não existe: `caixas` fica 0 e o rodapé nem mostra.
      return { caixas: acc.caixas + (emPesagem ? 0 : v.quantidadeCaixas), kg: acc.kg + kg, valor: acc.valor + kg * preco };
    },
    { caixas: 0, kg: 0, valor: 0 },
  );

  function addItem(produtoId: string) {
    const produto = produtoMap.get(produtoId);
    // Preço da praça como PONTO DE PARTIDA, nunca como imposição: entra só na
    // criação do item (`precoKg` sempre vazio aqui) e o usuário sobrescreve
    // digitando. Reabrir uma venda salva não passa por aqui — o preço fechado com
    // o comprador jamais pode ser trocado por um preço de referência.
    const daPraca = precoDaPraca(produtoId);
    setItens(prev => ({
      ...prev,
      [produtoId]: {
        quantidadeCaixas: 0,
        precoKg: daPraca != null ? daPraca.toFixed(2).replace(".", ",") : "",
        pesoCaixaKg: produto?.pesoCaixaKg ?? "",
        kg: "",
      },
    }));
  }
  function removeItem(produtoId: string) {
    setItens(prev => { const next = { ...prev }; delete next[produtoId]; return next; });
  }
  function bump(produtoId: string, delta: number) {
    setItens(prev => ({ ...prev, [produtoId]: { ...prev[produtoId], quantidadeCaixas: Math.max(0, (prev[produtoId]?.quantidadeCaixas ?? 0) + delta) } }));
  }
  function setCaixas(produtoId: string, valor: string) {
    setItens(prev => ({ ...prev, [produtoId]: { ...prev[produtoId], quantidadeCaixas: Math.max(0, parseInt(valor) || 0) } }));
  }
  function setPreco(produtoId: string, valor: string) {
    setItens(prev => ({ ...prev, [produtoId]: { ...prev[produtoId], precoKg: valor } }));
  }
  function setPesoCaixa(produtoId: string, valor: string) {
    setItens(prev => ({ ...prev, [produtoId]: { ...prev[produtoId], pesoCaixaKg: valor } }));
  }
  function setKg(produtoId: string, valor: string) {
    setItens(prev => ({ ...prev, [produtoId]: { ...prev[produtoId], kg: valor } }));
  }
  function bumpKg(produtoId: string, delta: number) {
    setItens(prev => {
      const atual = parseNum(prev[produtoId]?.kg ?? "");
      return { ...prev, [produtoId]: { ...prev[produtoId], kg: String(Math.max(0, atual + delta)) } };
    });
  }

  /**
   * Troca de modo — a assimetria é DE PROPÓSITO.
   *
   * padrão → variável: o kg calculado (caixas × peso) vira o kg digitado. Nenhum número que o
   * usuário viu muda de valor, então não se pergunta nada.
   * variável → padrão: o kg passa a ser RECALCULADO por caixas × peso do cadastro. É a única
   * troca que reescreve um número MEDIDO na balança — essa pergunta.
   */
  function trocarModo(novo: ModoPeso) {
    if (novo === modoPeso) return;
    if (novo === "PESAGEM") {
      setItens(prev => {
        const next: Record<string, ItemState> = {};
        for (const [produtoId, v] of Object.entries(prev)) {
          const produto = produtoMap.get(produtoId);
          const kg = produto ? v.quantidadeCaixas * pesoCaixaResolvido(v, produto) : 0;
          next[produtoId] = { ...v, kg: kg > 0 ? String(kg) : v.kg, quantidadeCaixas: 0 };
        }
        return next;
      });
      setModoPeso("PESAGEM");
      return;
    }
    setConfirmarVoltaCaixa(true);
  }

  function confirmarVoltaParaCaixaPadrao() {
    setItens(prev => {
      const next: Record<string, ItemState> = {};
      for (const [produtoId, v] of Object.entries(prev)) {
        const produto = produtoMap.get(produtoId);
        const pesoCaixa = produto ? parseFloat(produto.pesoCaixaKg) : 0;
        const kg = parseNum(v.kg);
        next[produtoId] = {
          ...v,
          pesoCaixaKg: produto?.pesoCaixaKg ?? "",
          quantidadeCaixas: pesoCaixa > 0 && kg > 0 ? Math.round(kg / pesoCaixa) : 0,
        };
      }
      return next;
    });
    setModoPeso("CAIXA_PADRAO");
    setConfirmarVoltaCaixa(false);
  }

  async function resolveCompradorId(): Promise<string | null> {
    if (compradorId === "__novo__") {
      if (!novoCompradorNome.trim()) {
        toast({ title: "Informe o nome do comprador", variant: "destructive" });
        return null;
      }
      const criado = await createComprador.mutateAsync({ nome: novoCompradorNome.trim() });
      setCompradorId(criado.id);
      return criado.id;
    }
    if (!compradorId) {
      toast({ title: "Selecione um comprador", variant: "destructive" });
      return null;
    }
    return compradorId;
  }

  /** `null` quando a data serve — a MESMA checagem que o servidor faz. */
  const falhaVencimento = vencimento ? checarVencimento(vencimento, data) : null;

  function buildPayload(compradorIdResolvido: string) {
    return {
      data,
      compradorId: compradorIdResolvido,
      vencimento: vencimento || null,
      motorista: motorista.trim() || null,
      placa: placa.trim() || null,
      // Vendas novas gravam a associação revisável (dia,setor); `colheitaInicio`/`colheitaFim`
      // ficam deprecated, só lidos de vendas antigas (spec §3.1).
      colheitaAssociacoes: Array.from(associacoesMarcadas).map(chave => {
        const [dataAssoc, setorId] = chave.split("|");
        return { data: dataAssoc, setorId };
      }),
      // Payload EXCLUSIVO por modo: o schema é uma união discriminada e recusa campo
      // estranho (`.strict()`), então mandar caixas junto de um item PESAGEM é 400, não
      // aceito-e-ignorado. É o servidor deixando de decidir em silêncio.
      itens: emPesagem
        ? itensAtivos
            .filter(([, v]) => parseNum(v.kg) > 0)
            .map(([produtoVendaId, v]) => ({
              modoPeso: "PESAGEM" as const,
              produtoVendaId,
              pesoTotalKg: String(parseNum(v.kg)),
              precoKg: v.precoKg,
            }))
        : itensAtivos
            .filter(([, v]) => v.quantidadeCaixas > 0)
            .map(([produtoVendaId, v]) => ({
              modoPeso: "CAIXA_PADRAO" as const,
              produtoVendaId,
              quantidadeCaixas: v.quantidadeCaixas,
              precoKg: v.precoKg,
              // Ausente (undefined) faz o servidor cair no peso padrão cadastrado do produto.
              pesoCaixaKg: v.pesoCaixaKg || undefined,
            })),
    };
  }

  async function handleSalvar(emitir: boolean) {
    if (submetendoRef.current) return;
    submetendoRef.current = true;
    try {
      const compradorIdResolvido = await resolveCompradorId();
      if (!compradorIdResolvido) return;
      const payload = buildPayload(compradorIdResolvido);
      if (payload.itens.length === 0) {
        toast({ title: "Adicione ao menos um item", variant: "destructive" });
        return;
      }

      let vendaId = id;
      if (isEditing) {
        await updateVenda.mutateAsync({ id: id!, data: payload });
      } else {
        const criada = await createVenda.mutateAsync(payload as any);
        vendaId = criada.id;
      }

      if (emitir && vendaId) {
        await emitirVenda.mutateAsync(vendaId);
      }

      const jaEstavaEmitida = isEditing && venda?.status === "EMITIDA";
      toast({ title: emitir ? "Venda emitida!" : jaEstavaEmitida ? "Alterações salvas!" : "Rascunho salvo!" });
      // Salvar EDIÇÃO = voltar: o detalhe já foi invalidado pelo useMutation, então remonta
      // atualizado — e o "voltar" do navegador não passa mais pelo formulário.
      // Salvar CRIAÇÃO = `replace` do /novo pelo /:id PRESERVANDO o state, para que o
      // "Voltar" do detalhe recém-criado ainda leve à lista filtrada de onde tudo começou.
      if (isEditing) voltar(`/cacho/vendas/${vendaId}`);
      else substituir(`/cacho/vendas/${vendaId}`);
    } catch (e: any) {
      toast({ title: "Erro ao salvar venda", description: e.message, variant: "destructive" });
    } finally {
      submetendoRef.current = false;
    }
  }

  const salvando = createVenda.isPending || updateVenda.isPending || emitirVenda.isPending || createComprador.isPending;

  // `salvando` só cobria as MUTATIONS. Enquanto as queries não chegam, o rodapé mostra
  // "0 caixas · 0 kg · R$ 0,00" e o comprador em branco — com o salvar clicável. O payload
  // sairia correto (os itens já estão no estado e o buildPayload não passa pelo produtoMap),
  // então não é perda de dado; é a tela mentindo num botão de ESCRITA, e nesta tela já se
  // pagou caro por acreditar no que ela mostrava.
  const carregando = carregandoVenda || carregandoProdutos || carregandoCompradores;

  /** Cancelar = voltar. Fallback: o detalhe em edição, a lista em venda nova. */
  function sair() {
    voltar(isEditing ? `/cacho/vendas/${id}` : "/cacho/vendas");
  }
  const fmt = (n: number) => n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtI = (n: number) => n.toLocaleString("pt-BR");

  return (
    /* Coluna única COM TETO (1024px). `max-w-2xl` era uma faixa de 670px no meio
       de 1900; a grade de duas colunas que a substituiu tirou a colheita do fluxo
       de leitura e mudava a grade no meio da página. O teto resolve sem nenhum
       dos dois. */
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      {/* Mesma posição e ícone do "Voltar" de venda-detalhe.tsx. O form não tinha nenhuma
          saída visível — sobravam o botão do navegador e o menu lateral —, e o único botão do
          rodapé em edição era o verde que ESCREVE. Nesta tela especificamente (a que reescrevia
          a associação em silêncio até a #147), a gramática empurrava pro lado errado. */}
      <Button variant="ghost" onClick={sair} className="w-fit -ml-2" data-testid="button-back">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <PageHeader
        titulo={isEditing ? "Editar venda" : "Nova venda"}
        descricao="Preencha durante o carregamento — salve como rascunho e emita quando fechar a carga."
      />

      <div className="rounded-lg border bg-card shadow-xs p-4 space-y-3">
        {/* ⚠️ UMA COLUNA NO CELULAR. O Safari do iPhone escreve a data por extenso
            ("12 de set. de 2026"), e item de grid tem `min-width: auto`: a
            coluna não encolhe abaixo do texto, então o campo transbordava a
            faixa e o picklist do Comprador subia POR CIMA dele. `min-w-0`
            sozinho resolveria a sobreposição encolhendo a caixa — e aí a data
            ficaria cortada, que é pior. Em 640px+ o Chrome usa o formato curto
            e as duas colunas cabem. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 min-w-0">
            <Label className="text-xs uppercase text-muted-foreground">Data</Label>
            <Input type="date" className="w-full" value={data} onChange={e => alterarDataVenda(e.target.value)} data-testid="input-data-venda" />
          </div>
          <div className="space-y-1 min-w-0">
            <Label className="text-xs uppercase text-muted-foreground">Comprador</Label>
            <Select value={compradorId} onValueChange={setCompradorId}>
              <SelectTrigger data-testid="select-comprador"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {compradores.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                <SelectItem value="__novo__">+ Novo comprador…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* O prazo nasce aqui quando já está combinado — é o momento em que o
              produtor fecha preço e prazo com o comprador. OPCIONAL de
              propósito: em branco fica "a definir", e se resolve depois no card
              da venda ou na linha de "A receber", sem travar o carregamento.
              `min` é a data da venda: o prazo pode estar no passado (dívida
              atrasada), nunca antes de a carga sair. */}
          <div className="space-y-1 min-w-0">
            <Label className="text-xs uppercase text-muted-foreground">Vencimento</Label>
            <Input
              type="date"
              className="w-full"
              value={vencimento}
              min={data}
              onChange={e => setVencimento(e.target.value)}
              data-testid="input-vencimento-venda"
            />
            {/* `min` cobre o picker, mas não o caso que morde: prazo já digitado
                e DEPOIS a data da venda empurrada para frente. Sem esta linha, o
                produtor só descobriria no 422 ao salvar. Mesma função do
                servidor — uma regra, dois lugares que a mostram. */}
            {falhaVencimento && (
              <p className="text-[11px] text-destructive" data-testid="erro-vencimento-venda">
                {mensagemDeFalhaDeData(falhaVencimento, "vencimento")}
              </p>
            )}
          </div>
        </div>
        {compradorId === "__novo__" && (
          <Input placeholder="Nome do comprador" value={novoCompradorNome} onChange={e => setNovoCompradorNome(e.target.value)} data-testid="input-novo-comprador" />
        )}
        <details>
          <summary className="text-xs text-muted-foreground cursor-pointer">Motorista e placa (opcional)</summary>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Input placeholder="Motorista" value={motorista} onChange={e => setMotorista(e.target.value)} />
            <Input placeholder="Placa" value={placa} onChange={e => setPlaca(e.target.value)} />
          </div>
        </details>
      </div>

      <div className="rounded-lg bg-sucesso-suave border border-sucesso/30 p-4 text-sm text-sucesso space-y-2.5" data-testid="card-colheita-associacao">
        {/* Primeira linha na HORIZONTAL em md+: o que está associado à esquerda,
            os presets de período à direita. Empilhado, os chips ficavam a uma
            linha de distância do número que eles mudam. */}
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="min-w-0">
        🌾 <b>Colheita associada:</b>{" "}
        {colheitasPorDiaSetor ? `${totalCachosMarcados} cachos · ${resumoMarcados}` : "carregando…"}
        <span className="ml-2 text-[10px] bg-card rounded-full px-2 py-0.5 text-muted-foreground">base do rateio</span>
        </div>
        <div className="flex gap-1.5 md:shrink-0">
          {(["hoje", "ontem-hoje", "custom"] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => aplicarPreset(p)}
              className={`text-xs font-semibold rounded-full px-3 py-1 border ${preset === p ? "bg-primary text-primary-foreground border-primary" : "bg-card border-sucesso/30 text-sucesso"}`}
              data-testid={`chip-preset-${p}`}
            >
              {p === "hoje" ? "Hoje" : p === "ontem-hoje" ? "Ontem + hoje" : "Personalizar…"}
            </button>
          ))}
        </div>
        </div>
        {preset === "custom" && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input type="date" value={colheitaInicio} onChange={e => alterarPeriodoManual("inicio", e.target.value)} data-testid="input-colheita-inicio" />
            <Input type="date" value={colheitaFim} onChange={e => alterarPeriodoManual("fim", e.target.value)} data-testid="input-colheita-fim" />
          </div>
        )}
        {/* Modo edição não é modo criação: o que está no período é DADO SALVO, não sugestão.
            Os chips continuam ali porque ampliar a janela é útil — mas o padrão é o período
            já associado, e isso precisa estar dito. */}
        {isEditing && (
          <p className="mt-2 text-[11px] text-sucesso/70" data-testid="texto-periodo-salvo">
            período da colheita já associada a esta venda
          </p>
        )}

        {/* Não bloqueia o salvar — é conferência, não trava. */}
        {diasPosterioresAVenda.length > 0 && (
          <p className="mt-2 text-[11px] text-atencao bg-atencao-suave border border-atencao/30 rounded-lg px-2 py-1.5" data-testid="aviso-colheita-posterior">
            ⚠️ {diasPosterioresAVenda.length === 1 ? "1 dia de colheita é posterior" : `${diasPosterioresAVenda.length} dias de colheita são posteriores`} à
            data da venda ({diasPosterioresAVenda.map(d => d.split("-").reverse().join("/")).join(", ")} &gt; {data.split("-").reverse().join("/")}) — confira se está certo.
          </p>
        )}

        {colheitasPorDiaSetor && colheitasPorDiaSetor.length === 0 && (
          <p className="mt-2 text-xs text-sucesso/70">Nenhuma colheita registrada nesse período.</p>
        )}

        {gruposPorDia.length > 0 && (
          <div className="mt-3 space-y-2">
            {gruposPorDia.map(([diaData, grupos]) => (
              <div key={diaData} className="rounded-lg bg-card border border-sucesso/30 p-2">
                <p className="text-xs font-bold mb-1">{diaData.split("-").reverse().join("/")}</p>
                <div className="space-y-1">
                  {grupos.map(g => {
                    const chave = `${g.data}|${g.setorId}`;
                    const marcado = associacoesMarcadas.has(chave);
                    return (
                      <label key={chave} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`check-associacao-${chave}`}>
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => toggleAssociacao(g.data, g.setorId)}
                          className="h-4 w-4"
                        />
                        <span className="font-semibold">{g.setorNome}</span>
                        <span className="text-muted-foreground">{g.cachos} cachos</span>
                        {/* Sem o filtro pelo próprio `id`, editar a 034 mostrava "já em #34"
                            — a venda em conflito consigo mesma. `getColheitasPorDiaSetor` é
                            um endpoint por período e não sabe qual venda está aberta, então o
                            filtro é do client. O badge existe pra sinalizar conflito REAL;
                            incluir a própria venda gastava o sinal à toa. */}
                        {outrasVendasNoGrupo(g).length > 0 && (
                          <span className="ml-auto text-[10px] bg-atencao-suave text-atencao rounded-full px-2 py-0.5" data-testid={`badge-ja-associado-${chave}`}>
                            já em #{outrasVendasNoGrupo(g).map(a => a.numero).join(", #")}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {associacoesForaDoPeriodo.length > 0 && (
          <div className="mt-3 rounded-lg bg-atencao-suave border border-atencao/30 p-2" data-testid="bloco-associacoes-fora-periodo">
            <p className="text-xs font-bold mb-1 text-atencao">Fora do período selecionado</p>
            <div className="space-y-1">
              {associacoesForaDoPeriodo.map(a => (
                <label key={a.chave} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`check-associacao-fora-${a.chave}`}>
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggleAssociacao(a.data, a.setorId)}
                    className="h-4 w-4"
                  />
                  <span>{a.data.split("-").reverse().join("/")} · <span className="font-semibold">{a.setorNome}</span> · {a.cachos} cachos</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-atencao/80">Amplie o período acima ou desmarque.</p>
          </div>
        )}
      </div>

      <div>
        {/* Rótulo e seletor de modo na MESMA linha em md+ — duas linhas para um
            rótulo de três palavras e um segmentado de dois botões era altura
            gasta à toa numa tela que se preenche com o caminhão parado. */}
        <div className="flex flex-col gap-2 mb-2 md:flex-row md:items-center md:justify-between md:gap-4">
          <Label className="text-xs uppercase text-muted-foreground">Adicionar produto</Label>

          {/* Seletor da CARGA. Em peso variável a caixa não é uma grandeza da venda: não é campo
              opcional, não é média derivada — não existe. */}
          <Segmented
            prefixoTestId="chip-modo"
            valor={modoPeso}
            onChange={trocarModo}
            opcoes={[
              { id: "CAIXA_PADRAO", label: "Caixa padrão" },
              { id: "PESAGEM", label: "Peso variável" },
            ]}
          />
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          {emPesagem
            ? "Cada caixa é pesada e os pesos são somados na nota. Entra só o kg por classificação e o preço."
            : "A caixa tem peso conhecido: você conta caixas e o kg vem por multiplicação."}
        </p>
        {/* Referência da praça ao lado dos preços. Some quando não há cotação. */}
        <div className="mb-3"><CardPracaHoje dataVenda={data} /></div>

        <div className="flex flex-wrap gap-2 mb-3">
          {produtos.map((p: ProdutoVenda) => (
            <button
              key={p.id}
              type="button"
              disabled={!!itens[p.id]}
              onClick={() => addItem(p.id)}
              className={`rounded-full border-2 border-dashed px-3 py-1.5 text-sm font-semibold text-primary ${itens[p.id] ? "opacity-30 line-through pointer-events-none" : ""}`}
              data-testid={`chip-produto-${p.id}`}
            >
              + {p.nome}
            </button>
          ))}
        </div>

      {/* Duas colunas em md+: o card do item é um bloco compacto (input de 5rem,
          botões de 44px) e esticado a 976px ficaria com dois terços de vazio à
          direita. Com o teto de 1024px a largura da coluna é estável, então
          `md:grid-cols-2` basta — não precisa do `auto-fill`. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
          {Object.keys(itens).map(produtoId => {
            const produto = produtoMap.get(produtoId);
            const it = itens[produtoId];
            if (!produto) return null;
            const pesoCaixa = pesoCaixaResolvido(it, produto);
            const kg = kgDoItem(it, produto);
            const preco = parseNum(it.precoKg);
            const valor = kg * preco;

            if (emPesagem) {
              return (
                <div key={produtoId} className="rounded-lg border bg-card shadow-xs p-3" data-testid={`item-${produtoId}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-sm">{produto.nome}</span>
                    <button type="button" onClick={() => removeItem(produtoId)}><X className="h-4 w-4 text-muted-foreground" /></button>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      className="flex-1 text-center font-bold text-lg h-11"
                      inputMode="decimal"
                      placeholder="0"
                      value={it.kg}
                      onChange={e => setKg(produtoId, e.target.value)}
                      data-testid={`input-kg-${produtoId}`}
                    />
                    <span className="text-sm font-semibold text-muted-foreground">kg</span>
                    <button type="button" onClick={() => bumpKg(produtoId, 10)} className="h-11 px-3 rounded-lg border text-sm font-semibold">+10</button>
                    <button type="button" onClick={() => bumpKg(produtoId, 100)} className="h-11 px-3 rounded-lg border text-sm font-semibold">+100</button>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">R$/kg</span>
                    <Input
                      className="w-24 text-right font-semibold"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={it.precoKg}
                      onChange={e => setPreco(produtoId, e.target.value)}
                      data-testid={`input-preco-${produtoId}`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground"><b className="text-foreground">{fmtI(kg)} kg</b> × {fmt(preco)} = <b className="text-foreground">R$ {fmt(valor)}</b></p>
                </div>
              );
            }

            return (
              <div key={produtoId} className="rounded-lg border bg-card shadow-xs p-3" data-testid={`item-${produtoId}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-sm">{produto.nome}</span>
                  <button type="button" onClick={() => removeItem(produtoId)}><X className="h-4 w-4 text-muted-foreground" /></button>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => bump(produtoId, -1)} className="w-11 h-11 rounded-lg border font-bold text-lg">−</button>
                  <Input
                    className="w-20 text-center font-bold text-lg h-11"
                    inputMode="numeric"
                    value={it.quantidadeCaixas}
                    onChange={e => setCaixas(produtoId, e.target.value)}
                  />
                  <button type="button" onClick={() => bump(produtoId, 1)} className="w-11 h-11 rounded-lg border font-bold text-lg">+</button>
                  <button type="button" onClick={() => bump(produtoId, 10)} className="h-11 px-3 rounded-lg border text-sm font-semibold">+10</button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{it.quantidadeCaixas} cx × {fmt(pesoCaixa)} kg = <b className="text-foreground">{fmtI(kg)} kg</b></p>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Peso/caixa (kg)</span>
                  <Input
                    className="w-24 text-right font-semibold"
                    inputMode="decimal"
                    placeholder={produto.pesoCaixaKg}
                    value={it.pesoCaixaKg}
                    onChange={e => setPesoCaixa(produtoId, e.target.value)}
                    data-testid={`input-peso-caixa-${produtoId}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">R$/kg</span>
                  <Input
                    className="w-24 text-right font-semibold"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={it.precoKg}
                    onChange={e => setPreco(produtoId, e.target.value)}
                  />
                  <span className="ml-auto font-bold text-sm">{valor ? `R$ ${fmt(valor)}` : ""}</span>
                </div>
              </div>
            );
          })}
      </div>
      </div>

      {/* Sticky (não fixed): gruda no fundo da própria coluna de conteúdo rolável (<main>
          do AppLayout), em vez do viewport inteiro — "fixed" ficava desalinhado da sidebar
          em telas largas e cobria o último item sem espaço de rolagem suficiente.

          ⚠️ SEM `-mx-4 md:-mx-6`: era o que fazia a barra vazar a largura do
          conteúdo e virar uma faixa de ponta a ponta, mais larga que o próprio
          formulário. Agora ela tem exatamente a largura do container. */}
      {/* A ÚNICA troca que reescreve um número medido na balança — por isso pergunta.
          O caminho inverso (padrão → variável) não muda valor nenhum e não pergunta nada. */}
      <Dialog open={confirmarVoltaCaixa} onOpenChange={setConfirmarVoltaCaixa}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Voltar para caixa padrão?</DialogTitle>
            <DialogDescription>
              O peso/caixa volta a ser o do cadastro de cada produto e o <b>kg passa a ser recalculado</b>{" "}
              por caixas × peso. Os quilos digitados agora (medidos na balança) serão substituídos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarVoltaCaixa(false)}>Cancelar</Button>
            <Button onClick={confirmarVoltaParaCaixaPadrao} data-testid="button-confirmar-volta-caixa">
              Voltar para caixa padrão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="sticky bottom-0 py-3.5 bg-background border-t flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {/* Em md+ os totais ficam À ESQUERDA, na mesma linha dos botões — é o uso
            horizontal da largura. No celular continuam numa linha própria acima. */}
        <div className="flex justify-between items-baseline gap-3 md:justify-start md:gap-2">
          {/* Em pesagem a caixa some do rodapé também — não há contagem a mostrar. */}
          <span className="text-xs text-muted-foreground">
            {emPesagem ? `${fmtI(totais.kg)} kg` : `${fmtI(totais.caixas)} caixas · ${fmtI(totais.kg)} kg`}
          </span>
          <span className="text-xl font-extrabold text-primary">R$ {fmt(totais.valor)}</span>
        </div>
        {/* Sair fácil, salvar de propósito — é a assimetria certa nesta tela.
            SEM confirmação de descarte, e isso é decisão, não esquecimento: o carregamento
            chega em etapas (venda, produtos, compradores) e um dirty-tracking ingênuo daria
            falso positivo sem o usuário ter tocado em nada, treinando a clicar em "descartar"
            no automático. Se um dia virar necessário, aí sim com dirty-tracking de verdade. */}
        {/* Em md+, LARGURA NATURAL e um só botão cheio: `flex-1` em três botões
            dava Cancelar do tamanho de Emitir, ~470px cada. No celular empilham em
            largura cheia, com a ação principal no topo — por isso a ordem do DOM
            é Emitir → Salvar → Cancelar e o desktop usa `md:flex-row-reverse`
            para devolver Emitir à direita. */}
        {isEditing && venda?.status === "EMITIDA" ? (
          // Venda já emitida — não faz sentido oferecer "salvar rascunho" nem reemitir
          // (o servidor já rejeitaria: só RASCUNHO pode emitir). Só salvar as alterações.
          <div className="flex flex-col gap-2 md:flex-row-reverse md:items-center md:shrink-0">
            <Button className="h-11 w-full md:w-auto md:min-w-[11rem]" disabled={salvando || carregando || !!falhaVencimento} onClick={() => handleSalvar(false)} data-testid="button-salvar-alteracoes">
              Salvar alterações
            </Button>
            <Button variant="ghost" className="h-11 w-full md:w-auto" onClick={sair} data-testid="button-cancelar">
              Cancelar
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 md:flex-row-reverse md:items-center md:shrink-0">
            <Button className="h-11 w-full md:w-auto md:min-w-[11rem]" disabled={salvando || carregando || !!falhaVencimento} onClick={() => handleSalvar(true)} data-testid="button-emitir">
              Emitir ▸
            </Button>
            <Button variant="outline" className="h-11 w-full md:w-auto" disabled={salvando || carregando || !!falhaVencimento} onClick={() => handleSalvar(false)} data-testid="button-salvar-rascunho">
              Salvar rascunho
            </Button>
            <Button variant="ghost" className="h-11 w-full md:w-auto" onClick={sair} data-testid="button-cancelar">
              Cancelar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
